import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import ProductCard from './ProductCard';
import { request } from '../utils/request';
import { Product as MockProduct, Category } from '../data/mockData';
import { ApiProduct, ApiTemplate } from '../utils/resourceUtils';
import { 
  ShoppingBagIcon, 
  SparklesIcon, 
  TruckIcon, 
  ShieldCheckIcon,
  PaintBrushIcon,
  ExclamationTriangleIcon,
  ChevronLeftIcon,
  ChevronRightIcon
} from '@heroicons/react/24/outline';

// Interface for featured products API response
interface FeaturedProductsResponse {
  success: boolean;
  code: number;
  message: string;
  result: {
    products: ApiProduct[];
    pagination: {
      page: number;
      limit: number;
      total: number;
      pages: number;
    };
  };
}

// Interface for categories API response
interface CategoriesResponse {
  success: boolean;
  code: number;
  message: string;
  result: Category[];
}

// Interface for carousel item
interface CarouselItem {
  background: string;
  title: string;
  description: string;
  url: string;
}

const HomePage: React.FC = () => {
  const navigate = useNavigate();
  const [featuredProducts, setFeaturedProducts] = useState<MockProduct[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingCategories, setIsLoadingCategories] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [categoriesError, setCategoriesError] = useState<string | null>(null);
  
  // Carousel state
  const [carouselItems, setCarouselItems] = useState<CarouselItem[]>([]);
  const [currentCarouselIndex, setCurrentCarouselIndex] = useState(0);
  const [isCarouselAutoPlaying, setIsCarouselAutoPlaying] = useState(true);

  // Load categories from API
  const loadCategories = async () => {
    setIsLoadingCategories(true);
    setCategoriesError(null);
    
    try {
      const response: CategoriesResponse = await request({
        url: 'categories',
        method: 'GET'
      });
      
      if (response.success) {
        setCategories(response.result);
      } else {
        setCategoriesError('Failed to load categories');
      }
    } catch (error) {
      console.error('Load categories error:', error);
      setCategoriesError('Failed to load categories');
    } finally {
      setIsLoadingCategories(false);
    }
  };

  // Load featured products from API
  const loadFeaturedProducts = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      const response: FeaturedProductsResponse = await request({
        url: 'products?featured=true&limit=3',
        method: 'GET'
      });
      
      if (response.success) {
        // Transform API products to match frontend Product interface
        const transformedProducts: MockProduct[] = response.result.products.map((apiProduct: ApiProduct) => ({
          id: apiProduct.id.toString(),
          name: apiProduct.name,
          description: apiProduct.description || '',
          category: apiProduct.category,
          price: apiProduct.basePrice || apiProduct.price || 0,
          image: apiProduct.image || '',
          rating: apiProduct.rating || 0,
          reviews: apiProduct.reviews || 0,
          customizable: true, // Default to true for API products
          templates: (apiProduct.templates || []).map((template: ApiTemplate) => ({
            id: template.id.toString(),
            name: template.name,
            preview: '', // API doesn't provide preview, use empty string
            design: template.design
          })),
          colors: [], // Default empty colors array
          sizes: [], // Default empty sizes array
          previews: apiProduct.previews || []
        }));
        
        setFeaturedProducts(transformedProducts);
      } else {
        setError(response.message || 'Failed to load featured products');
      }
    } catch (error: any) {
      console.error('Load featured products error:', error);
      setError('Failed to load featured products');
    } finally {
      setIsLoading(false);
    }
  };

  // Carousel navigation functions
  const goToNextSlide = () => {
    setCurrentCarouselIndex((prev) => 
      prev === carouselItems.length - 1 ? 0 : prev + 1
    );
  };

  const goToPrevSlide = () => {
    setCurrentCarouselIndex((prev) => 
      prev === 0 ? carouselItems.length - 1 : prev - 1
    );
  };

  const goToSlide = (index: number) => {
    setCurrentCarouselIndex(index);
  };

  // Auto-play carousel
  useEffect(() => {
    if (!isCarouselAutoPlaying || carouselItems.length <= 1) return;

    const interval = setInterval(() => {
      goToNextSlide();
    }, 5000); // Change slide every 5 seconds

    return () => clearInterval(interval);
  }, [currentCarouselIndex, isCarouselAutoPlaying, carouselItems.length]);

  // Load categories and featured products on component mount
  useEffect(() => {
    loadCategories();
    loadFeaturedProducts();
  }, []);

  // Load banners for hero carousel
  useEffect(() => {
    const loadBanners = async () => {
      try {
        const response: any = await request({ url: 'products/banners', method: 'GET' });
        if (response?.success && Array.isArray(response.result)) {
          const items = (response.result as any[]).map((b) => ({
            background: b.background,
            title: b.title,
            description: b.description,
            url: b.url || '/products'
          }));
          setCarouselItems(items);
        } else {
          // Fallback to a default slide if API returns empty
          setCarouselItems([
            
          ]);
        }
      } catch (e) {
        // Fallback on error
        setCarouselItems([
          {
            background: 'images/background3.jpg',
            title: 'Create Your Perfect Design',
            description: 'Customize t-shirts, mugs, posters, and more with our easy-to-use design tool',
            url: '/products'
          }
        ]);
      }
    };
    loadBanners();
  }, []);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Carousel Section */}
      <section className="relative h-[200px] sm:h-[300px] md:h-[400px] overflow-hidden">
        {carouselItems.length > 0 && (
          <>
            {/* Carousel Slides */}
            <div className="relative h-full">
              {carouselItems.map((item, index) => (
                <div
                  key={index}
                  className={`absolute inset-0 transition-opacity duration-500 ${
                    index === currentCarouselIndex ? 'opacity-100' : 'opacity-0'
                  }`}
                >
                  <div
                    className="absolute inset-0 bg-cover bg-center bg-no-repeat"
                    style={{
                      backgroundImage: `url(${item.background})`,
                      backgroundSize: 'cover',
                      backgroundPosition: 'center'
                    }}
                  >
                    {/* Overlay gradient */}
                    <div className="absolute inset-0 bg-gradient-to-r from-black/60 to-black/40"></div>
                  </div>
                  <div className="relative h-full flex items-center justify-center">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-white">
                      <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold mb-4 sm:mb-6">
                        {item.title}
                      </h1>
                      <p className="text-lg sm:text-xl md:text-2xl mb-6 sm:mb-8 opacity-90 max-w-3xl mx-auto">
                        {item.description}
                      </p>
                      <Link
                        to={item.url}
                        className="inline-flex items-center px-6 sm:px-8 py-3 sm:py-4 bg-white font-semibold rounded-lg hover:bg-gray-100 transition-colors"
                        style={{ color: '#2e3a47' }}
                      >
                        <SparklesIcon className="w-5 h-5 mr-2" />
                        Start Creating
                      </Link>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Navigation Arrows */}
            {carouselItems.length > 1 && (
              <>
                <button
                  onClick={goToPrevSlide}
                  onMouseEnter={() => setIsCarouselAutoPlaying(false)}
                  onMouseLeave={() => setIsCarouselAutoPlaying(true)}
                  className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 sm:w-12 sm:h-12 bg-white/80 hover:bg-white rounded-full flex items-center justify-center shadow-lg transition-all z-10"
                  aria-label="Previous slide"
                >
                  <ChevronLeftIcon className="w-5 h-5 sm:w-6 sm:h-6 text-gray-800" />
                </button>
                <button
                  onClick={goToNextSlide}
                  onMouseEnter={() => setIsCarouselAutoPlaying(false)}
                  onMouseLeave={() => setIsCarouselAutoPlaying(true)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 sm:w-12 sm:h-12 bg-white/80 hover:bg-white rounded-full flex items-center justify-center shadow-lg transition-all z-10"
                  aria-label="Next slide"
                >
                  <ChevronRightIcon className="w-5 h-5 sm:w-6 sm:h-6 text-gray-800" />
                </button>
              </>
            )}

            {/* Carousel Indicators */}
            {carouselItems.length > 1 && (
              <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex space-x-2 z-10">
                {carouselItems.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => goToSlide(index)}
                    onMouseEnter={() => setIsCarouselAutoPlaying(false)}
                    onMouseLeave={() => setIsCarouselAutoPlaying(true)}
                    className={`w-2 h-2 sm:w-3 sm:h-3 rounded-full transition-all ${
                      index === currentCarouselIndex
                        ? 'bg-white w-6 sm:w-8'
                        : 'bg-white/50 hover:bg-white/75'
                    }`}
                    aria-label={`Go to slide ${index + 1}`}
                  />
                ))}
              </div>
            )}
          </>
        )}
      </section>

      {/* Categories Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-center mb-12">Shop by Category</h2>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-6">
            {categories.map((category) => (
              <Link
                key={category.id}
                to={`/products?category=${category.id}`}
                className="group block text-center p-6 rounded-lg border-2 border-gray-200 hover:shadow-lg transition-all"
                style={{ borderColor: 'inherit' }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = '#2e3a47';
                  const title = e.currentTarget.querySelector('h3');
                  if (title) title.style.color = '#2e3a47';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = '';
                  const title = e.currentTarget.querySelector('h3');
                  if (title) title.style.color = '';
                }}
              >
                <div className="text-4xl mb-4 group-hover:scale-110 transition-transform">
                  {category.icon}
                </div>
                <h3 className="text-lg font-semibold text-gray-900">
                  {category.name}
                </h3>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Products */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-center mb-12">Featured Products</h2>
          
          {/* Loading State */}
          {isLoading && (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 mx-auto mb-4" style={{ borderBottomColor: '#2e3a47' }}></div>
              <p className="text-gray-600">Loading featured products...</p>
            </div>
          )}

          {/* Error State */}
          {error && (
            <div className="text-center py-12">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <ExclamationTriangleIcon className="w-8 h-8 text-red-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Error</h3>
              <p className="text-gray-600 mb-6">{error}</p>
              <button
                onClick={loadFeaturedProducts}
                className="inline-flex items-center px-6 py-3 text-white rounded-lg transition-colors"
                style={{ backgroundColor: '#2e3a47' }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#3a4a57'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#2e3a47'}
              >
                Try Again
              </button>
            </div>
          )}

          {/* Products Grid */}
          {!isLoading && !error && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {featuredProducts.map((product) => (
                <ProductCard
                  key={product.id}
                  product={product}
                  viewMode="grid"
                  hasResource={false}
                />
              ))}
            </div>
          )}

          {/* Empty State */}
          {!isLoading && !error && featuredProducts.length === 0 && (
            <div className="text-center py-12">
              <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4" style={{ backgroundColor: '#f0f2f5' }}>
                <PaintBrushIcon className="w-8 h-8" style={{ color: '#2e3a47' }} />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">No featured products</h3>
              <p className="text-gray-600 mb-6">
                Check back later for our latest featured designs.
              </p>
              <Link
                to="/products"
                className="inline-flex items-center px-6 py-3 text-white rounded-lg transition-colors"
                style={{ backgroundColor: '#2e3a47' }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#3a4a57'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#2e3a47'}
              >
                Browse All Products
              </Link>
            </div>
          )}
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-center mb-12">Why Choose Us</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4" style={{ backgroundColor: '#f0f2f5' }}>
                <SparklesIcon className="w-8 h-8" style={{ color: '#2e3a47' }} />
              </div>
              <h3 className="text-lg font-semibold mb-2">Easy Design Tool</h3>
              <p className="text-gray-600">
                Intuitive drag-and-drop interface for creating custom designs
              </p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <ShieldCheckIcon className="w-8 h-8 text-green-600" />
              </div>
              <h3 className="text-lg font-semibold mb-2">Quality Guarantee</h3>
              <p className="text-gray-600">
                Premium materials and printing quality for lasting results
              </p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <TruckIcon className="w-8 h-8 text-purple-600" />
              </div>
              <h3 className="text-lg font-semibold mb-2">Fast Shipping</h3>
              <p className="text-gray-600">
                Quick turnaround times and reliable delivery worldwide
              </p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <ShoppingBagIcon className="w-8 h-8 text-orange-600" />
              </div>
              <h3 className="text-lg font-semibold mb-2">Wide Selection</h3>
              <p className="text-gray-600">
                Hundreds of products and templates to choose from
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage; 